"use client";

import { Check, Loader2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StepId, StepStatus } from "./types";
import { STEP_ORDER, STEP_META } from "./types";

interface StepProgressProps {
  stepStatuses: Record<StepId, StepStatus>;
  onStepClick?: (stepId: StepId) => void;
}

export function StepProgress({ stepStatuses, onStepClick }: StepProgressProps) {
  return (
    <div className="flex items-center justify-between gap-1">
      {STEP_ORDER.map((stepId, idx) => {
        const status = stepStatuses[stepId];
        const meta = STEP_META[stepId];
        const isLast = idx === STEP_ORDER.length - 1;
        const isClickable = status === "completed" && onStepClick && stepId !== "connect";

        return (
          <div key={stepId} className="flex items-center flex-1 last:flex-none">
            <div
              className={cn(
                "flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0",
                status === "completed" && "bg-green-500 text-white",
                status === "in-progress" && "bg-primary text-primary-foreground",
                status === "active" && "bg-primary/20 text-primary border-2 border-primary",
                status === "locked" && "bg-muted text-muted-foreground",
                isClickable && "cursor-pointer hover:ring-2 hover:ring-green-400/50"
              )}
              title={meta.title}
              onClick={isClickable ? () => { onStepClick(stepId); } : undefined}
              role={isClickable ? "button" : undefined}
            >
              {status === "completed" ? (
                <Check className="w-3.5 h-3.5" />
              ) : status === "in-progress" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : status === "locked" ? (
                <Lock className="w-3 h-3" />
              ) : (
                meta.number
              )}
            </div>
            {!isLast && (
              <div
                className={cn(
                  "h-0.5 flex-1 mx-1",
                  status === "completed" ? "bg-green-500/50" : "bg-border"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
